import { useCallback } from 'react';
import type { PincodeLookup, PlaceDetailRow, PlaceSuggestionRow } from '@dru/types';
import { AddressFieldset, type AddressFormValue } from '@dru/ui';
import { api } from '../lib/api';
import { useQuery } from '../lib/useQuery';
import { useAuth } from '../lib/auth';

/**
 * The shared address form, wired to this app's API.
 *
 * `AddressFieldset` in `@dru/ui` knows nothing about endpoints; this is the
 * one place the pincode directory and the map search are plugged in, so a
 * company, a plant, a partner branch and a camp venue all fill an address
 * the same way. A screen that needs an address imports this and nothing
 * else.
 *
 * Map search appears only when the server says a key is configured. Without
 * one the pincode still does the work, and coordinates can be pasted.
 *
 * The pin map is OpenStreetMap (MapPin), which needs no key; it is shown
 * where a precise point is worth having and the form is not read-only.
 */

export function AddressFields({
  value, onChange, compact, errors, required, pincodeRequired, disabled, hideCountry, hideLandmark, autoLocate, brief,
}: {
  value: AddressFormValue;
  onChange: (next: AddressFormValue) => void;
  compact?: boolean;
  errors?: Record<string, string | undefined>;
  required?: boolean;
  /** The pincode alone is required; it fills city, district and state. */
  pincodeRequired?: boolean;
  disabled?: boolean;
  hideCountry?: boolean;
  hideLandmark?: boolean;
  autoLocate?: boolean;
  /** Pincode, city, state and one line only. */
  brief?: boolean;
}) {
  // Every geo route needs geo.lookup. Without it the form is plain manual
  // entry — no auto-fill, no map search — instead of a 403 on each render and
  // on each pincode typed (Plant HR adding an employee hit both).
  const canLookup = useAuth().permissions.has('geo.lookup');
  const places = useQuery<{ configured: boolean }>(
    () => (canLookup
      ? api.get<{ configured: boolean }>('/geo/places/status').catch(() => ({ configured: false }))
      : Promise.resolve({ configured: false })),
    [canLookup],
  );

  const lookupPincode = useCallback(
    (pin: string) => api.get<PincodeLookup>(`/geo/pincodes/${pin}`), [],
  );
  const searchPlaces = useCallback(async (q: string) => {
    const res = await api.get<{ items: PlaceSuggestionRow[] }>(`/geo/places?q=${encodeURIComponent(q)}`);
    return res.items;
  }, []);
  const resolvePlace = useCallback(
    (placeId: string) => api.get<PlaceDetailRow>(`/geo/places/${encodeURIComponent(placeId)}`), [],
  );

  return (
    <AddressFieldset
      value={value}
      onChange={onChange}
      compact={compact}
      errors={errors}
      required={required}
      pincodeRequired={pincodeRequired}
      disabled={disabled}
      lookupPincode={canLookup ? lookupPincode : undefined}
      searchPlaces={places.data?.configured ? searchPlaces : undefined}
      resolvePlace={places.data?.configured ? resolvePlace : undefined}
      withMap={!disabled}
      hideCountry={hideCountry}
      hideLandmark={hideLandmark}
      autoLocate={autoLocate}
      brief={brief}
    />
  );
}
